import { Modal, Form, Switch, Input, Row, Col, message } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { permissionsApi } from '@/api/permissions'
import type { Hospital } from '@/types/hospital'
import type { UserPermission, PermissionFlags } from '@/types/permission'
import { getApiErrorMessage } from '@/utils/error'

interface PermissionModalProps {
  open: boolean
  onCancel: () => void
  userId: string
  hospital: Hospital
  existingPermission?: UserPermission
}

export const PermissionModal = ({
  open,
  onCancel,
  userId,
  hospital,
  existingPermission,
}: PermissionModalProps) => {
  const [form] = Form.useForm()
  const queryClient = useQueryClient()

  const isEdit = !!existingPermission

  const setPermissionMutation = useMutation({
    mutationFn: ({ flags }: { flags: PermissionFlags }) =>
      permissionsApi.setFacilityPermission(userId, hospital.id, {
        cmsid: hospital.cmsid,
        ...flags,
      }),
    onSuccess: () => {
      message.success(
        isEdit ? 'Permissions updated successfully' : 'Permissions granted successfully'
      )
      queryClient.invalidateQueries({ queryKey: ['user-permissions', userId] })
      queryClient.invalidateQueries({ queryKey: ['hospitals-bulk'] })
      onCancel()
      form.resetFields()
    },
    onError: (error: unknown) => {
      message.error(getApiErrorMessage(error, 'Failed to save permissions'))
    },
  })

  const handleOk = async () => {
    try {
      const values = await form.validateFields()
      setPermissionMutation.mutate({ flags: values })
    } catch (_error) {
      // Form validation failed
    }
  }

  const handleCancel = () => {
    form.resetFields()
    onCancel()
  }

  return (
    <Modal
      title={
        isEdit ? `Edit Permissions - ${hospital.name}` : `Grant Permissions - ${hospital.name}`
      }
      open={open}
      onOk={handleOk}
      onCancel={handleCancel}
      confirmLoading={setPermissionMutation.isPending}
      okText={isEdit ? 'Update' : 'Grant'}
      width={600}
    >
      <div style={{ marginBottom: 16, color: '#888' }}>
        {hospital.city}, {hospital.state} • CMS ID: {hospital.cmsid}
      </div>

      <Form
        form={form}
        layout="vertical"
        initialValues={
          existingPermission || {
            admin: false,
            sender: false,
            receiver: false,
            readonly: true,
            bi: false,
            dashboard: false,
            teamInsights: false,
          }
        }
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="admin"
              label="Admin"
              valuePropName="checked"
              tooltip="Full administrative access to this facility"
            >
              <Switch />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="sender"
              label="Sender"
              valuePropName="checked"
              tooltip="Can send patient transfers"
            >
              <Switch />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="receiver"
              label="Receiver"
              valuePropName="checked"
              tooltip="Can receive patient transfers"
            >
              <Switch />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="readonly"
              label="Read Only"
              valuePropName="checked"
              tooltip="View-only access to data"
            >
              <Switch />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="bi"
              label="BI (Business Intelligence)"
              valuePropName="checked"
              tooltip="Access to analytics and reports"
            >
              <Switch />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="dashboard"
              label="Dashboard"
              valuePropName="checked"
              tooltip="Access to dashboards and metrics"
            >
              <Switch />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="teamInsights"
              label="Team Insights"
              valuePropName="checked"
              tooltip="Access to team insights dashboard"
            >
              <Switch />
            </Form.Item>
          </Col>
        </Row>

        {!isEdit && (
          <Form.Item
            name="reason"
            label="Reason (Optional)"
            tooltip="Provide a reason for granting these permissions"
          >
            <Input.TextArea rows={3} placeholder="E.g., User needs access for training session" />
          </Form.Item>
        )}
      </Form>
    </Modal>
  )
}
